import imgLoader from '../util/imgLoader';
import { promisify } from '../util/utilities';
import BaseDecorator from './baseDecorator';

/**
 * MediaContainer decorator that extends BaseDecorator to handle resizing of media elements.
 * Automatically loads and resizes video and image elements within the container.
 * @extends BaseDecorator
 */
export default class MediaContainer extends BaseDecorator {
	get updatedClassName() {
		return 'resized';
	}

	/**
	 * Schedules an update operation after all media elements have loaded.
	 * Waits for videos to load metadata and images to fully load.
	 * @returns {Promise<any>} Promise that resolves when update is scheduled
	 */
	scheduleUpdate() {
		this.logger.log(`[${this.scheduleUpdate.name}]`);

		const loadPromises = this.$el
			.children()
			.toArray()
			.map((/** @type {HTMLVideoElement | HTMLImageElement | HTMLElement} */ child) => {
				const { tagName } = child;
				if (tagName === 'VIDEO') {
					if (/** @type {HTMLVideoElement} */ (child).readyState === 4) {
						return Promise.resolve();
					}

					return promisify(child, 'loadedmetadata', 'addEventListener');
				}

				if (tagName === 'IMG') {
					return imgLoader
						.loadImage(/** @type {HTMLImageElement} */ (child))
						.catch((/** @type {Error} */ error) => {
							this.logger.warn('MediaContainer failed to load image', {
								image: child,
							});
							return Promise.reject(error);
						});
				}

				return Promise.resolve();
			});

		return Promise.all(loadPromises).then(this.startUpdate.bind(this));
	}

	/**
	 * Resizes the container to fit the largest media element within it.
	 * Implements the update method from BaseDecorator.
	 * @returns {Promise<any>} Promise that resolves when update is complete
	 */
	update() {
		this.logger.log(`[${this.update.name}]`);

		const { width, height } = this.$el
			.children()
			.toArray()
			.reduce(
				(
					/** @type {{ width: number; height: number; }} */ maxSize,
					/** @type {HTMLImageElement | HTMLElement | HTMLVideoElement} */ child,
				) => {
					const { width: childWidth, height: childHeight } = this.getChildSize(child);
					return {
						width: Math.max(childWidth, maxSize.width),
						height: Math.max(childHeight, maxSize.height),
					};
				},
				{ width: 0, height: 0 },
			);

		this.$el.css({ width, height });

		return Promise.resolve();
	}

	/**
	 * @param {HTMLImageElement | HTMLVideoElement | HTMLElement} child
	 * @returns {{width: number, height: number}}
	 */
	getChildSize(child) {
		this.logger.log(`[${this.getChildSize.name}]`, { child });

		// Get the container dimensions - use innerWidth/innerHeight to get content area only
		const containerWidth = this.$el.innerWidth();
		const containerHeight = this.$el.innerHeight();

		let mediaWidth;
		let mediaHeight;
		if (child instanceof HTMLImageElement) {
			mediaWidth = child.naturalWidth;
			mediaHeight = child.naturalHeight;
		} else if (child instanceof HTMLVideoElement) {
			mediaWidth = child.videoWidth;
			mediaHeight = child.videoHeight;
		}
		const src = child.attributes.getNamedItem('src')?.value;
		this.logger.debug({ mediaWidth, mediaHeight, src });

		// Get computed styles to account for borders
		const computedStyle = window.getComputedStyle(child);
		const borderLeftWidth = parseInt(computedStyle.borderLeftWidth) || 0;
		const borderRightWidth = parseInt(computedStyle.borderRightWidth) || 0;
		const borderTopWidth = parseInt(computedStyle.borderTopWidth) || 0;
		const borderBottomWidth = parseInt(computedStyle.borderBottomWidth) || 0;

		// Calculate total width and height including borders
		const totalBorderWidth = borderLeftWidth + borderRightWidth;
		const totalBorderHeight = borderTopWidth + borderBottomWidth;
		this.logger.debug({ totalBorderWidth, totalBorderHeight });

		if (!mediaWidth || !mediaHeight || !containerWidth || !containerHeight) {
			if (src) {
				this.logger.warn('Invalid dimensions for resizing', {
					child,
					containerWidth,
					containerHeight,
					mediaWidth,
					mediaHeight,
				});
			}
			return { width: 0, height: 0 };
		}

		const scale = Math.min(
			(containerWidth - totalBorderWidth) / mediaWidth,
			(containerHeight - totalBorderHeight) / mediaHeight,
		);

		// Apply scale to the content dimensions (media + borders)
		const newWidth = mediaWidth * scale + totalBorderWidth;
		const newHeight = mediaHeight * scale + totalBorderHeight;
		this.logger.debug({ scale, newWidth, newHeight });

		return { width: newWidth, height: newHeight };
	}
}
